import { useRef } from 'react';

import { useGlass } from '@components/glass/useGlass';
import { Icon } from '@components/ui/Icon';
import { THEME_LABELS } from '@config/theme';
import { useSquashOnChange } from '@hooks/useSquashOnChange';
import { useResolvedTheme } from '@stores/index';
import { themeManager } from '@systems/ThemeManager';
import { cx } from '@utils/cx';

import styles from './ThemeToggle.module.scss';

/**
 * Light/dark switch: a glass pill and a glass bubble that slides along it.
 *
 * Dark: the bubble rests left holding a glowing moon; the track reads
 * "Light". Light: the bubble rests right holding a dimmed sun; the track
 * reads "Dark". The label always names where a click will take you.
 *
 * The switch itself, in order:
 *   - the bubble glides across (CSS translate, smooth, no overshoot)
 *   - it stretches along its travel and eases back (useSquashOnChange)
 *   - the icon turns: one rotates out as the other rotates in
 *   - the labels cross-fade
 *   - the bubble refracts the label text as it passes over it
 *
 * The bubble is taller than the track, so it reads as a drop resting on
 * the pill rather than a knob sunk into it.
 *
 * `data-motion="essential"` keeps the glide and the cross-fades running
 * under reduced motion, as iOS does for its switches: small, state-carrying
 * motion. The stretch and the icon rotation still switch off there.
 *
 * Exposed as a switch labelled "Dark mode", which is how assistive
 * technology expects an on/off setting to be announced; the visible
 * words and icons are decorative. The page cross-fades to the new theme
 * while the bubble glides (see ThemeManager.setPreference).
 */
export const ThemeToggle = ({ className }: { readonly className?: string }) => {
  const isDark = useResolvedTheme() === 'dark';
  const trackRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  /* Not `interactive`: no hover lift or pointer glare. The switch responds
     to the click itself, with the glide and the theme change. */
  const track = useGlass(trackRef, { variant: 'clear', className: cx(styles.track, className) });
  const bubble = useGlass(bubbleRef, { variant: 'clear', className: styles.bubble });
  useSquashOnChange(bubbleRef, isDark);

  const handleClick = (): void => {
    themeManager.setPreference(isDark ? 'light' : 'dark');
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Dark mode"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      ref={trackRef}
      className={track.className}
      style={track.style}
      data-glass-surface=""
      data-motion="essential"
      onClick={handleClick}
    >
      {track.filter}
      <span className={cx(styles.label, styles.toLight)} aria-hidden="true">
        {THEME_LABELS.light}
      </span>
      <span className={cx(styles.label, styles.toDark)} aria-hidden="true">
        {THEME_LABELS.dark}
      </span>
      <span ref={bubbleRef} className={bubble.className} style={bubble.style} aria-hidden="true">
        {bubble.filter}
        <span className={styles.halo} />
        <Icon name="moon" className={cx(styles.icon, styles.moon)} />
        <Icon name="sun" className={cx(styles.icon, styles.sun)} />
      </span>
    </button>
  );
};
